/**
 * Dimensioni delle foto dei luoghi.
 *
 * Le immagini non stanno sul nostro server: il database conserva soltanto
 * l'indirizzo della foto su Wikimedia, e il telefono la scarica da li' quando
 * serve. Resta pero' un problema di traffico: l'indirizzo salvato punta alla
 * versione da 1280 px (~330 KB) e usarla per una miniatura da 72 px vorrebbe
 * dire far scaricare trecento kilobyte per un francobollo — in viaggio, spesso
 * in roaming, con la connessione che va e viene.
 *
 * Wikimedia genera le miniature solo in alcune larghezze prestabilite: chiederne
 * una diversa non rimpicciolisce l'immagine, restituisce un errore 400. Queste
 * sono quelle utilizzabili.
 */
const LARGHEZZE_AMMESSE = [120, 250, 500, 1280] as const;

export type LarghezzaFoto = (typeof LARGHEZZE_AMMESSE)[number];

/** `.../thumb/d/de/Nome.jpg/1280px-Nome.jpg` → larghezza e ricostruzione. */
const THUMB = /^(.*\/thumb\/.*\/)(\d+)px-([^/]+)$/;

/*
 * Nota: le foto che non arrivano da Wikimedia (per esempio da Openverse) non
 * hanno miniature prestabilite. `fotoLarga` le lascia intatte e il browser le
 * ridimensiona: qualche kilobyte in piu' su pochi luoghi, in cambio di nessun
 * indirizzo inventato.
 */

/**
 * Indirizzo della stessa foto nella larghezza richiesta.
 *
 * Due cautele:
 *
 *   * se l'indirizzo non e' una miniatura di Wikimedia (per esempio e' il file
 *     originale) si restituisce com'e': meglio qualche kilobyte in piu' che una
 *     foto che non si carica;
 *   * non si chiede mai una versione piu' grande di quella salvata, perche'
 *     Wikimedia non ingrandisce oltre l'originale e risponderebbe con un errore.
 */
export function fotoLarga(url: string, larghezza: LarghezzaFoto): string {
  const match = THUMB.exec(url);
  if (match === null) return url;

  const [, prefisso, attualeRaw, nome] = match;
  if (prefisso === undefined || attualeRaw === undefined || nome === undefined) return url;

  const attuale = Number(attualeRaw);
  if (!Number.isFinite(attuale)) return url;

  const scelta = larghezza <= attuale ? larghezza : attuale;
  if (scelta === attuale) return url;

  return `${prefisso}${String(scelta)}px-${nome}`;
}
